import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  MapPin, Search, ArrowLeft, ChevronRight, Flag, Trophy, BarChart3,
  Loader2, X, Clock, Navigation2, CircleDot, Share2, Crosshair
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import TooltipGuide from '../components/TooltipGuide';
import { GPSMapView } from '../components/play/GPSMapView';
import { ScorecardMini } from '../components/play/ScorecardMini';
import { HoleScoringView } from '../components/play/HoleScoringView';
import { RoundSummary } from '../components/play/RoundSummary';
import { StatsView } from '../components/play/StatsView';
import { API } from '../components/play/constants';

const Play = () => {
  const [view, setView] = useState('menu');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [nearbyCity, setNearbyCity] = useState('');
  const [nearbyCourses, setNearbyCourses] = useState([]);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [loadingCourse, setLoadingCourse] = useState(false);
  const [selectedTee, setSelectedTee] = useState(null);
  const [numHoles, setNumHoles] = useState(18);
  const [activeRound, setActiveRound] = useState(null);
  const [currentHole, setCurrentHole] = useState(1);
  const [golfStats, setGolfStats] = useState(null);
  const [roundHistory, setRoundHistory] = useState([]);
  const [recentScores, setRecentScores] = useState([]);
  const [viewingRound, setViewingRound] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [sharingRound, setSharingRound] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [playingTab, setPlayingTab] = useState('score');
  const [draftScores, setDraftScores] = useState({});
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [roundShots, setRoundShots] = useState([]);


  useEffect(() => {
    const checkActive = async () => {
      try {
        const res = await axios.get(`${API}/golf/rounds/active`);
        if (res.data.round) {
          setActiveRound(res.data.round);
          setRoundShots(res.data.round.shots || []);
          const scored = res.data.round.hole_scores || [];
          setCurrentHole(scored.length > 0 ? Math.min(scored.length + 1, res.data.round.num_holes) : 1);
          setView('playing');
        }
      } catch { /* silent */ }
    };
    checkActive();
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, histRes] = await Promise.all([
        axios.get(`${API}/golf/stats`),
        axios.get(`${API}/golf/rounds/history`)
      ]);
      setGolfStats(statsRes.data.stats);
      setRecentScores(statsRes.data.recent_scores || []);
      setRoundHistory(histRes.data.rounds || []);
    } catch { /* silent */ }
  };

  const fetchNearbyCourses = useCallback(() => {
    if (nearbyCourses.length > 0) return;
    if (!navigator.geolocation) return;
    setLoadingNearby(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await axios.get(`${API}/golf/courses/nearby`, {
            params: { lat: pos.coords.latitude, lng: pos.coords.longitude }
          });
          setNearbyCourses(res.data.courses || []);
          setNearbyCity(res.data.city || '');
        } catch { /* nearby courses lookup is best-effort */ }
        finally { setLoadingNearby(false); }
      },
      () => { setLoadingNearby(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [nearbyCourses.length]);

  const searchCourses = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/golf/courses/search`, { params: { q: searchQuery } });
      setSearchResults(res.data.courses || []);
      setAutocompleteResults([]);
    } catch (e) { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  const handleSearchInput = (val) => {
    setSearchQuery(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (val.trim().length < 2) { setAutocompleteResults([]); return; }
    setAutocompleteLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/golf/courses/search`, { params: { q: val } });
        setAutocompleteResults(res.data.courses || []);
      } catch {}
      finally { setAutocompleteLoading(false); }
    }, 400);
  };

  const handleDraftChange = useCallback((holeNumber, data) => {
    setDraftScores(prev => ({ ...prev, [holeNumber]: data }));
  }, []);

  const logShot = useCallback(async (shotData) => {
    if (!activeRound) return;
    try {
      await axios.post(`${API}/golf/rounds/shot`, { round_id: activeRound.id, ...shotData });
      setRoundShots(prev => [...prev, shotData]);
      toast.success(`Shot ${shotData.shot_number} logged (${shotData.club})`);
    } catch (e) { toast.error('Failed to log shot'); }
  }, [activeRound]);

  const selectCourse = async (course) => {
    setSelectedCourse(course);
    setLoadingCourse(true);
    try {
      const res = await axios.get(`${API}/golf/courses/${course.id}`);
      setCourseDetails(res.data.course);
      setView('tee_select');
    } catch (e) { toast.error('Failed to load course'); }
    finally { setLoadingCourse(false); }
  };

  const startRound = async () => {
    if (!selectedTee || !courseDetails) return;
    const holes = selectedTee.holes.slice(0, numHoles).map((h, i) => ({
      hole_number: i + 1, par: h.par, yardage: h.yardage, handicap: h.handicap || 0,
    }));
    try {
      const res = await axios.post(`${API}/golf/rounds/start`, {
        course_id: courseDetails.id, course_name: courseDetails.course_name || courseDetails.club_name,
        club_name: courseDetails.club_name, tee_name: selectedTee.tee_name,
        num_holes: numHoles, holes_data: holes,
        latitude: courseDetails.location?.latitude, longitude: courseDetails.location?.longitude,
      });
      setActiveRound(res.data.round);
      setRoundShots([]);
      setCurrentHole(1);
      setView('playing');
      toast.success('Round started!');
    } catch (e) { toast.error('Failed to start round'); }
  };

  const saveHoleScore = async (scoreData) => {
    if (!activeRound) return;
    try {
      await axios.post(`${API}/golf/rounds/score`, { round_id: activeRound.id, ...scoreData });
      const updated = { ...activeRound };
      const scores = [...(updated.hole_scores || [])];
      const idx = scores.findIndex(s => s.hole_number === scoreData.hole_number);
      const entry = { ...scoreData, score_to_par: scoreData.strokes - scoreData.par, fairway_hit: scoreData.par >= 4 ? scoreData.drive_accuracy === 'hit' : null, gir: (scoreData.strokes - scoreData.putts) <= (scoreData.par - 2) };
      if (idx >= 0) scores[idx] = entry; else scores.push(entry);
      scores.sort((a, b) => a.hole_number - b.hole_number);
      updated.hole_scores = scores;
      setActiveRound(updated);
      setDraftScores(prev => { const next = { ...prev }; delete next[scoreData.hole_number]; return next; });
      if (currentHole < activeRound.num_holes) {
        setCurrentHole(currentHole + 1);
        toast.success(`Hole ${scoreData.hole_number} submitted`);
      } else {
        toast.success('Last hole submitted!');
      }
    } catch (e) { toast.error('Failed to save score'); }
  };

  const completeRound = async () => {
    if (!activeRound) return;
    try {
      const res = await axios.post(`${API}/golf/rounds/${activeRound.id}/complete`);
      const completedRound = { ...activeRound, summary: res.data.summary, status: 'completed' };
      setViewingRound(completedRound);
      setActiveRound(null);
      setView('summary');
      fetchStats();
      toast.success('Round complete!');
      setSharingRound(completedRound);
      setShowShareDialog(true);
    } catch (e) { toast.error('Failed to complete round'); }
  };

  const abandonRound = async () => {
    if (!activeRound) return;
    try {
      await axios.delete(`${API}/golf/rounds/${activeRound.id}`);
      setActiveRound(null);
      setView('menu');
      toast('Round discarded');
    } catch { toast.error('Failed to quit round'); }
  };

  const shareRoundToFeed = async () => {
    if (!sharingRound) return;
    setSharing(true);
    try {
      const s = sharingRound.summary || {};
      const toPar = s.score_to_par;
      const toParStr = toPar === 0 ? 'E' : toPar > 0 ? `+${toPar}` : `${toPar}`;
      const content = `Shot ${s.total_strokes} (${toParStr}) at ${sharingRound.course_name}`;
      await axios.post(`${API}/social/posts`, {
        content,
        post_type: 'round',
        media_urls: [],
        round_id: sharingRound.id,
        round_summary: s,
        round_hole_scores: sharingRound.hole_scores || [],
        round_course_name: sharingRound.course_name,
        round_tee_name: sharingRound.tee_name,
        round_num_holes: sharingRound.num_holes,
        round_holes_data: sharingRound.holes_data || [],
      });
      toast.success('Shared to your feed!');
      setShowShareDialog(false);
      setSharingRound(null);
    } catch (e) { toast.error('Failed to share'); }
    finally { setSharing(false); }
  };

  // ===== Render: Menu =====
  const renderMenu = () => (
    <div className="space-y-4">
      <Button className="w-full h-14 text-base font-semibold bg-primary hover:bg-primary/90 rounded-xl shadow-md shadow-primary/20" onClick={() => { setView('search'); fetchNearbyCourses(); }} data-testid="start-round-btn">
        <Flag className="w-5 h-5 mr-2" /> Start New Round
      </Button>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1 rounded-xl border-primary/20 hover:bg-primary/5 hover:border-primary/40" onClick={() => { fetchStats(); setView('stats'); }} data-testid="view-stats-btn">
          <BarChart3 className="w-4 h-4 mr-2" /> Stats
        </Button>
        <Button variant="outline" className="flex-1 rounded-xl border-primary/20 hover:bg-primary/5 hover:border-primary/40" onClick={() => { fetchStats(); setView('stats'); }} data-testid="view-history-btn">
          <Clock className="w-4 h-4 mr-2" /> History
        </Button>
      </div>
      {roundHistory.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Recent Rounds</h3>
          <div className="space-y-2">
            {roundHistory.slice(0, 3).map(r => {
              const s = r.summary || {};
              const tp = s.score_to_par; const tpStr = tp === 0 ? 'E' : tp > 0 ? `+${tp}` : `${tp}`;
              return (
                <button key={r.id} onClick={() => { setViewingRound(r); setView('summary'); }} className="w-full flex items-center gap-3 p-3 rounded-xl bg-card border border-border/30 hover:border-primary/30 hover:shadow-sm transition-all text-left">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><span className="text-lg font-bold text-primary">{s.total_strokes}</span></div>
                  <div className="flex-1 min-w-0"><h4 className="text-sm font-medium text-foreground truncate">{r.course_name}</h4><p className="text-xs text-muted-foreground">{new Date(r.completed_at).toLocaleDateString()} · {tpStr}</p></div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // ===== Render: Course Search =====
  const renderSearch = () => {
    const displayCourses = searchResults.length > 0 ? searchResults : nearbyCourses;
    const showNearbyHeader = searchResults.length === 0 && nearbyCourses.length > 0 && autocompleteResults.length === 0;
    const showAutocomplete = autocompleteResults.length > 0 && searchResults.length === 0;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => { setView('menu'); setSearchResults([]); setAutocompleteResults([]); }} data-testid="search-back"><ArrowLeft className="w-5 h-5" /></Button>
          <h2 className="text-lg font-bold text-foreground">Find Course</h2>
        </div>
        <div className="relative">
          <div className="flex gap-2">
            <Input placeholder="Search courses..." value={searchQuery} onChange={(e) => handleSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchCourses()} className="flex-1 rounded-xl" data-testid="course-search-input" />
            <Button onClick={searchCourses} disabled={searching} className="rounded-xl" data-testid="course-search-btn">{searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
          </div>
          {showAutocomplete && (
            <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-card border border-border/50 rounded-xl shadow-lg max-h-64 overflow-y-auto" data-testid="autocomplete-dropdown">
              {autocompleteLoading && <div className="px-4 py-2 text-xs text-muted-foreground">Searching...</div>}
              {autocompleteResults.map(c => (
                <button key={c.id} onClick={() => { selectCourse(c); setAutocompleteResults([]); setSearchQuery(c.club_name || c.course_name); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors text-left border-b border-border/20 last:border-0"
                  data-testid={`autocomplete-${c.id}`}>
                  <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="min-w-0"><p className="text-sm font-medium text-foreground truncate">{c.club_name}{c.course_name && c.course_name !== c.club_name && <span className="text-primary font-bold"> — {c.course_name}</span>}</p>{c.location && <p className="text-xs text-muted-foreground">{c.location.city}, {c.location.state}</p>}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        {loadingNearby && <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Finding courses near you...</span></div>}
        {showNearbyHeader && <div className="flex items-center gap-2"><Navigation2 className="w-4 h-4 text-primary" /><h3 className="text-sm font-semibold text-foreground">Courses near {nearbyCity || 'you'}</h3></div>}
        {!showAutocomplete && (
          <div className="space-y-2">
            {displayCourses.map(c => (
              <button key={c.id} onClick={() => selectCourse(c)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all text-left" data-testid={`course-result-${c.id}`}>
                <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0"><h4 className="text-sm font-semibold text-foreground truncate">{c.club_name}{c.course_name && c.course_name !== c.club_name && <span className="text-primary font-bold"> — {c.course_name}</span>}</h4><p className="text-xs text-muted-foreground">{c.location?.city}, {c.location?.state}</p></div>
                {loadingCourse && selectedCourse?.id === c.id ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
              </button>
            ))}
          </div>
        )}
        {!loadingNearby && displayCourses.length === 0 && searchResults.length === 0 && !searchQuery && (
          <div className="text-center py-8 text-muted-foreground"><MapPin className="w-12 h-12 mx-auto mb-2 opacity-30" /><p className="text-sm">Enable location services to see nearby courses, or search by name</p></div>
        )}
      </div>
    );
  };

  // ===== Render: Tee Selection =====
  const renderTeeSelect = () => {
    if (!courseDetails) return null;
    const allTees = [...(courseDetails.tees?.male || []), ...(courseDetails.tees?.female || [])];
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setView('search')} data-testid="tee-back"><ArrowLeft className="w-5 h-5" /></Button>
          <div><h2 className="text-lg font-bold text-foreground">{courseDetails.club_name}{courseDetails.course_name && courseDetails.course_name !== courseDetails.club_name && <span className="text-primary"> — {courseDetails.course_name}</span>}</h2><p className="text-sm text-muted-foreground">{courseDetails.location?.city}, {courseDetails.location?.state}</p></div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-2">Select Tee</h3>
          <div className="space-y-2">
            {allTees.map((tee, i) => (
              <button key={i} onClick={() => setSelectedTee(tee)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${selectedTee === tee ? 'border-primary bg-primary/10' : 'border-border/50 bg-card hover:border-primary/30'}`}
                data-testid={`tee-option-${i}`}>
                <CircleDot className={`w-5 h-5 ${selectedTee === tee ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className="flex-1"><h4 className="text-sm font-semibold">{tee.tee_name}</h4><p className="text-xs text-muted-foreground">{tee.total_yards} yds · Par {tee.par_total} · Rating {tee.course_rating}/{tee.slope_rating}</p></div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-2">Holes</h3>
          <div className="flex gap-2">
            {[9, 18].map(n => (
              <button key={n} onClick={() => setNumHoles(n)}
                className={`flex-1 py-3 rounded-xl border text-center font-semibold transition-all ${numHoles === n ? 'border-primary bg-primary/10 text-primary' : 'border-border/50 bg-card text-muted-foreground hover:border-primary/30'}`}
                data-testid={`holes-${n}`}>{n} Holes</button>
            ))}
          </div>
        </div>
        <Button className="w-full h-12 text-base font-semibold bg-primary hover:bg-primary/90 rounded-xl" onClick={startRound} disabled={!selectedTee} data-testid="begin-round-btn">
          <Flag className="w-5 h-5 mr-2" /> Begin Round
        </Button>
      </div>
    );
  };

  // ===== Render: Active Round =====
  const renderPlaying = () => {
    if (!activeRound) return null;
    const holesData = activeRound.holes_data || [];
    const holeScores = activeRound.hole_scores || [];
    const currentHoleData = holesData[currentHole - 1] || { par: 4, yardage: 0 };
    const existingScore = holeScores.find(s => s.hole_number === currentHole);
    const allScored = holeScores.length >= activeRound.num_holes;

    const totalStrokes = holeScores.reduce((s, h) => s + h.strokes, 0);
    const totalPar = holeScores.reduce((s, h) => s + h.par, 0);
    const runningToPar = totalStrokes - totalPar;
    const runStr = runningToPar === 0 ? 'E' : runningToPar > 0 ? `+${runningToPar}` : `${runningToPar}`;

    return (
      <div className="space-y-3">
        <div className="bg-card rounded-xl border border-border/50 p-3 flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-bold text-foreground truncate">{activeRound.course_name}</h2>
            <p className="text-xs text-muted-foreground">{activeRound.tee_name}</p>
          </div>
          <div className="text-right pl-3">
            <div className={`text-2xl font-black ${runningToPar < 0 ? 'text-red-500' : runningToPar === 0 ? 'text-foreground' : 'text-blue-500'}`}>{runStr}</div>
            <p className="text-[10px] text-muted-foreground">{holeScores.length}/{activeRound.num_holes} holes</p>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border/50 p-1 flex gap-1">
          <button
            onClick={() => setPlayingTab('score')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${
              playingTab === 'score' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-score"
          >
            Score Entry
          </button>
          <button
            onClick={() => setPlayingTab('scorecard')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${
              playingTab === 'scorecard' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-scorecard"
          >
            Scorecard
          </button>
        </div>

        <Button variant="outline" onClick={() => setShowMap(true)} className="w-full rounded-xl gap-2 h-11 font-semibold" data-testid="toggle-gps">
          <Crosshair className="w-4 h-4 text-primary" /> GPS Rangefinder
        </Button>
        {showMap && (
          <GPSMapView
            activeRound={activeRound}
            currentHole={currentHole}
            totalHoles={activeRound.num_holes}
            holesData={holesData}
            onClose={() => setShowMap(false)}
            onHoleChange={setCurrentHole}
            shots={roundShots}
            onLogShot={logShot}
          />
        )}

        {playingTab === 'score' && (
          <HoleScoringView
            hole={currentHoleData} holeNumber={currentHole} totalHoles={activeRound.num_holes}
            existingScore={existingScore} draftScores={draftScores} onDraftChange={handleDraftChange}
            onSave={saveHoleScore}
            onPrev={() => setCurrentHole(Math.max(1, currentHole - 1))}
            onNext={() => setCurrentHole(Math.min(activeRound.num_holes, currentHole + 1))}
          />
        )}

        {playingTab === 'scorecard' && (
          <ScorecardMini holesData={holesData} holeScores={holeScores} currentHole={currentHole} expandable={true} onHoleClick={(n) => { setCurrentHole(n); setPlayingTab('score'); }} />
        )}

        {allScored && (
          <Button className="w-full h-12 text-base font-bold bg-green-600 hover:bg-green-700 text-white rounded-xl" onClick={completeRound} data-testid="complete-round-btn">
            <Trophy className="w-5 h-5 mr-2" /> Complete Round
          </Button>
        )}
        {!allScored && holeScores.length > 0 && (
          <Button variant="outline" className="w-full h-10 text-sm border-destructive/50 text-destructive hover:bg-destructive/10 rounded-xl" onClick={completeRound} data-testid="end-round-early-btn">
            <Flag className="w-4 h-4 mr-2" /> End Round ({holeScores.length}/{activeRound.num_holes} holes)
          </Button>
        )}
        <Button variant="ghost" className="w-full h-10 text-sm text-muted-foreground hover:text-destructive" onClick={abandonRound} data-testid="abandon-round-btn">
          <X className="w-4 h-4 mr-2" /> Quit Round
        </Button>
      </div>
    );
  };

  // ===== Main Render =====
  const renderContent = () => {
    if (view === 'menu') return renderMenu();
    if (view === 'search') return renderSearch();
    if (view === 'tee_select') return renderTeeSelect();
    if (view === 'playing') return renderPlaying();
    if (view === 'summary' && viewingRound) return <RoundSummary round={viewingRound} onClose={() => { setViewingRound(null); setView('menu'); }} />;
    if (view === 'stats') return <StatsView stats={golfStats} recentScores={recentScores} history={roundHistory} onBack={() => setView('menu')} onViewRound={(r) => { setViewingRound(r); setView('summary'); }} />;
    return renderMenu();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="play-page">
      <div className="max-w-lg mx-auto px-4 pt-6 pb-24">
        <div className="mb-5">
          <h1 className="font-serif text-2xl font-bold" style={{ color: 'var(--deep-forest)' }}>Play</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--warm-gray)' }}>Track your round, score, and GPS yardages</p>
        </div>
        {renderContent()}
      </div>

      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl border-border/50">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Trophy className="w-5 h-5 text-primary" /> Share Your Round
            </DialogTitle>
          </DialogHeader>
          {sharingRound && sharingRound.summary && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-2xl p-4 text-center">
                <p className="text-sm text-muted-foreground mb-1">{sharingRound.course_name}</p>
                <div className="text-4xl font-bold text-foreground">{sharingRound.summary.total_strokes}</div>
                <div className={`text-lg font-semibold ${sharingRound.summary.score_to_par < 0 ? 'text-red-500' : sharingRound.summary.score_to_par === 0 ? 'text-foreground' : 'text-blue-500'}`}>
                  {sharingRound.summary.score_to_par === 0 ? 'E' : sharingRound.summary.score_to_par > 0 ? `+${sharingRound.summary.score_to_par}` : sharingRound.summary.score_to_par}
                </div>
                <div className="flex justify-center gap-4 mt-3 text-xs text-muted-foreground">
                  <span>{sharingRound.summary.total_putts} putts</span>
                  <span>{sharingRound.summary.fairway_pct}% FW</span>
                  <span>{sharingRound.summary.gir_pct}% GIR</span>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowShareDialog(false)} className="flex-1 rounded-full" data-testid="skip-share-btn">Skip</Button>
                <Button onClick={shareRoundToFeed} disabled={sharing} className="flex-1 rounded-full gap-2" data-testid="share-round-btn">
                  <Share2 className="w-4 h-4" /> {sharing ? 'Sharing...' : 'Share to Feed'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TooltipGuide pageKey="play" />
      <Navigation />
    </div>
  );
};

export default Play;
